// Procedural pixel-art character sprites. Pure: draws into an RGBA buffer, no DOM,
// so it runs headless (tests, future server) and in the browser (via ImageData).
//
// Sheet layout: 4 columns (walk frames) x 4 rows (directions: down, left, right, up).
// Frame 0 and 2 are the standing pose; 1 and 3 are opposite steps.

import type { AvatarIdentity, AvatarTraits } from "@degen/sim/avatar/traits";
import { Rng } from "@degen/sim/rng";

export const FRAME_W = 24;
export const FRAME_H = 34;
export const FRAMES = 4;
export const DIRS = 4;
export const SHEET_W = FRAME_W * FRAMES;
export const SHEET_H = FRAME_H * DIRS;

export const DIR_DOWN = 0;
export const DIR_LEFT = 1;
export const DIR_RIGHT = 2;
export const DIR_UP = 3;
export type Dir = 0 | 1 | 2 | 3;

export interface SpriteSheet {
  width: number;
  height: number;
  data: Uint8ClampedArray; // RGBA
}

const OUTLINE = "#16121d";
const EYE = "#1b1b2a";
const GOLD = "#f5c518";
const DIAMOND = "#c6f6ff";

// ---------- color helpers ----------

function parse(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Multiply brightness (f < 1 darker, f > 1 lighter). */
export function shade(hex: string, f: number): string {
  const [r, g, b] = parse(hex);
  if (f <= 1) return toHex(r * f, g * f, b * f);
  const t = f - 1;
  return toHex(r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t);
}

// ---------- pixel frame ----------

/** One frame. Coordinates are in "design space": y is offset by 1 so hats can poke up. */
class Frame {
  readonly px: (string | null)[] = new Array(FRAME_W * FRAME_H).fill(null);
  clipped = 0;

  set(x: number, y: number, c: string) {
    y += 1;
    if (x < 0 || y < 0 || x >= FRAME_W || y >= FRAME_H) {
      this.clipped++;
      return;
    }
    this.px[y * FRAME_W + x] = c;
  }

  /** Inclusive rectangle. */
  rect(x0: number, y0: number, x1: number, y1: number, c: string) {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) this.set(x, y, c);
  }

  /** Add a 1px dark outline around every opaque shape. */
  outline() {
    const src = this.px.slice();
    for (let y = 0; y < FRAME_H; y++) {
      for (let x = 0; x < FRAME_W; x++) {
        if (src[y * FRAME_W + x]) continue;
        const n =
          (x > 0 && src[y * FRAME_W + x - 1]) ||
          (x < FRAME_W - 1 && src[y * FRAME_W + x + 1]) ||
          (y > 0 && src[(y - 1) * FRAME_W + x]) ||
          (y < FRAME_H - 1 && src[(y + 1) * FRAME_W + x]);
        if (n) this.px[y * FRAME_W + x] = OUTLINE;
      }
    }
  }
}

interface Ctx {
  f: Frame;
  t: AvatarTraits;
  female: boolean;
  frame: number; // 0..3
  noise: Rng; // only for the glitch mask, reseeded per frame so it's stable
}

// =====================================================================
// FRONT (facing the camera)
// =====================================================================

function drawFront(c: Ctx) {
  const { f, t, female, frame } = c;
  const tx0 = female ? 8 : 7;
  const tx1 = female ? 15 : 16;
  const liftR = frame === 1;
  const liftL = frame === 3;
  const armDyL = frame === 1 ? 1 : frame === 3 ? -1 : 0;
  const armDyR = -armDyL;

  // Long hair behind the body.
  if (!t.headwear.includes("hood") && (t.hair === "long" || t.hair === "wavy")) f.rect(5, 6, 18, 18, shade(t.hairColor, 0.75));

  drawLegsFront(c, tx0, tx1, liftL, liftR);

  // Torso
  const top = t.topColor;
  f.rect(tx0, 14, tx1, 22, top);
  f.rect(tx1, 14, tx1, 22, shade(top, 0.8));
  f.rect(tx0, 22, tx1, 22, shade(top, 0.85));
  switch (t.top) {
    case "hoodie":
      f.rect(tx0 + 2, 19, tx1 - 2, 20, shade(top, 0.82)); // pocket
      f.rect(10, 15, 10, 17, "#f4f4f5"); // drawstrings
      f.rect(13, 15, 13, 17, "#f4f4f5");
      break;
    case "jacket":
      f.rect(11, 14, 12, 22, "#e5e7eb");
      f.rect(10, 14, 10, 22, shade(top, 0.7));
      f.rect(13, 14, 13, 22, shade(top, 0.7));
      break;
    case "suit":
      f.rect(10, 14, 13, 16, "#f4f4f5");
      f.rect(11, 15, 12, 20, "#dc2626");
      f.set(11, 14, "#f4f4f5");
      break;
    case "tracksuit":
      f.rect(11, 14, 12, 18, shade(top, 1.35)); // zip line
      break;
    case "tee":
      f.rect(10, 14, 13, 14, shade(top, 0.75)); // collar
      break;
  }

  // Arms (sleeves + hands)
  const ax0 = female ? 6 : 5;
  const ax1 = female ? 16 : 17;
  drawArmFront(c, ax0, armDyL, false);
  drawArmFront(c, ax1, armDyR, true);

  // Neck jewelry
  if (t.neck !== "none") {
    const col = t.neck === "diamond" ? DIAMOND : GOLD;
    for (const [x, y] of [[9, 14], [10, 15], [11, 16], [12, 16], [13, 15], [14, 14]]) f.set(x, y, col);
    if (t.neck === "diamond") f.rect(11, 17, 12, 17, "#ffffff");
  }

  drawHeadFront(c);
}

function drawLegsFront(c: Ctx, tx0: number, tx1: number, liftL: boolean, liftR: boolean) {
  const { f, t, female } = c;
  const pants = t.bottomColor;
  const legW = female ? 1 : 2; // width - 1
  const lx = female ? 9 : 8;
  const rx = female ? 13 : 13;
  // Hips
  if (t.bottom === "skirt") f.rect(tx0 - 1, 22, tx1 + 1, 25, pants);
  else f.rect(tx0, 22, tx1, 23, pants);
  if (t.bottom === "skirt") f.rect(tx0 - 1, 25, tx1 + 1, 25, shade(pants, 0.8));
  const legColor = (y: number) => (t.bottom === "pants" || y < 26 ? pants : t.skin);
  const leg = (x: number, lifted: boolean) => {
    const bottom = lifted ? 28 : 29;
    for (let y = 24; y <= bottom; y++) {
      if (t.bottom === "skirt" && y <= 25) continue;
      f.rect(x, y, x + legW, y, legColor(y));
    }
    const sy = lifted ? 29 : 30;
    f.rect(x - (female ? 0 : 1), sy, x + legW, sy + 1, t.shoes);
    f.rect(x - (female ? 0 : 1), sy + 1, x + legW, sy + 1, shade(t.shoes, 0.75));
  };
  leg(lx, liftL);
  leg(rx, liftR);
}

function drawArmFront(c: Ctx, x: number, dy: number, right: boolean) {
  const { f, t } = c;
  const sleeve = t.topColor;
  const sleeveEnd = t.top === "tee" ? 16 : 20;
  for (let y = 14; y <= 20; y++) {
    const col = y <= sleeveEnd ? (right ? shade(sleeve, 0.85) : sleeve) : t.skin;
    f.rect(x, y + dy, x + 1, y + dy, col);
  }
  if (t.top === "tracksuit") f.rect(right ? x + 1 : x, 14 + dy, right ? x + 1 : x, 20 + dy, "#f4f4f5");
  f.rect(x, 21 + dy, x + 1, 22 + dy, t.skin);
}

function drawHeadFront(c: Ctx) {
  const { f, t } = c;
  const hooded = t.headwear === "hood";

  if (hooded) {
    const hood = t.headwearColor;
    f.rect(4, 1, 19, 14, hood);
    for (const [x, y] of [[4, 1], [19, 1], [4, 14], [19, 14], [5, 1], [18, 1]]) f.px[(y + 1) * FRAME_W + x] = null;
    f.rect(19, 2, 19, 13, shade(hood, 0.8));
    f.rect(5, 4, 18, 4, shade(hood, 1.15)); // rim highlight
  }

  // Head / face
  const fx0 = hooded ? 7 : 6;
  const fx1 = hooded ? 16 : 17;
  const fy0 = hooded ? 5 : 3;
  f.rect(fx0, fy0, fx1, 13, t.skin);
  f.rect(fx1, fy0, fx1, 13, shade(t.skin, 0.88));
  if (!hooded) {
    for (const [x, y] of [[6, 3], [17, 3], [6, 13], [17, 13]]) f.px[(y + 1) * FRAME_W + x] = null;
  }

  if (t.mask !== "none") {
    drawMaskFront(c, fx0, fy0, fx1);
  } else {
    drawFaceFront(c);
  }

  if (!hooded) {
    drawHairFront(c);
    drawHeadwearFront(c);
  }
  if (t.earring) f.set(5, 11, GOLD);
}

function drawFaceFront(c: Ctx) {
  const { f, t, female } = c;
  // Eyes
  f.rect(9, 8, 9, 9, EYE);
  f.rect(14, 8, 14, 9, EYE);
  f.set(9, 8, "#3a3a52");
  if (female) {
    f.set(8, 8, EYE);
    f.set(15, 8, EYE);
    f.set(8, 10, shade("#f39c9c", 1));
    f.set(15, 10, shade("#f39c9c", 1));
  }
  // Mouth
  f.rect(11, 11, 12, 11, shade(t.skin, 0.65));
  if (t.beard) {
    f.rect(7, 11, 16, 13, t.hairColor);
    f.rect(8, 10, 15, 10, t.hairColor);
    f.rect(11, 11, 12, 11, shade(t.hairColor, 0.6));
  }
  switch (t.eyes) {
    case "shades":
      f.rect(7, 8, 16, 9, "#0f0f14");
      f.set(8, 8, "#5b6b8c");
      f.set(13, 8, "#5b6b8c");
      break;
    case "laser":
      f.rect(9, 8, 10, 9, "#ff2a2a");
      f.rect(13, 8, 14, 9, "#ff2a2a");
      f.set(9, 8, "#ffe1e1");
      f.set(14, 8, "#ffe1e1");
      f.set(8, 9, "#ff7a7a");
      f.set(15, 9, "#ff7a7a");
      break;
    case "glasses3d":
      f.rect(7, 7, 16, 7, "#f4f4f5");
      f.rect(7, 8, 10, 9, "#ef4444");
      f.rect(13, 8, 16, 9, "#3b82f6");
      f.rect(11, 8, 12, 8, "#f4f4f5");
      break;
  }
}

function drawMaskFront(c: Ctx, x0: number, y0: number, x1: number) {
  const { f, t, noise } = c;
  switch (t.mask) {
    case "balaclava":
      f.rect(x0, y0, x1, 13, "#23232b");
      f.rect(8, 8, 15, 9, t.skin);
      f.rect(9, 8, 9, 9, EYE);
      f.rect(14, 8, 14, 9, EYE);
      break;
    case "blankmask":
      f.rect(x0, y0, x1, 13, "#f2f2f2");
      f.rect(x1, y0, x1, 13, "#c9c9cf");
      f.rect(9, 8, 10, 8, "#111");
      f.rect(13, 8, 14, 8, "#111");
      f.rect(10, 11, 13, 11, "#8a8a95");
      f.set(9, 10, "#8a8a95");
      f.set(14, 10, "#8a8a95");
      break;
    case "visor":
      f.rect(x0, 7, x1, 9, t.accent);
      f.rect(x0, 7, x1, 7, shade(t.accent, 1.5));
      f.rect(x0, 9, x1, 9, shade(t.accent, 0.7));
      f.rect(11, 11, 12, 11, shade(t.skin, 0.65));
      break;
    case "void":
      f.rect(x0, y0, x1, 13, "#0b0b10");
      f.rect(9, 8, 10, 8, t.accent);
      f.rect(13, 8, 14, 8, t.accent);
      break;
    case "glitch": {
      const pal = ["#0b0b10", t.accent, "#f4f4f5", shade(t.accent, 0.5), "#ff2bd6"];
      for (let y = y0; y <= 13; y++) for (let x = x0; x <= x1; x++) f.set(x, y, pal[noise.int(0, pal.length - 1)]);
      f.rect(9, 8, 9, 9, "#ffffff");
      f.rect(14, 8, 14, 9, "#ffffff");
      break;
    }
  }
}

function drawHairFront(c: Ctx) {
  const { f, t } = c;
  const h = t.hairColor;
  const hi = shade(h, 1.3);
  switch (t.hair) {
    case "bald":
      f.set(8, 4, shade(t.skin, 1.2));
      break;
    case "buzz":
      f.rect(6, 3, 17, 4, h);
      break;
    case "spiky":
      f.rect(6, 3, 17, 5, h);
      for (const x of [7, 10, 13, 16]) f.rect(x, 1, x + 1 > 17 ? 17 : x + 1, 2, h);
      f.rect(6, 6, 6, 7, h);
      f.rect(17, 6, 17, 7, h);
      break;
    case "sidepart":
      f.rect(5, 2, 18, 5, h);
      f.rect(6, 6, 10, 6, h);
      f.rect(5, 6, 5, 9, h);
      f.rect(18, 6, 18, 9, h);
      f.rect(12, 3, 16, 3, hi);
      break;
    case "messy":
      f.rect(5, 2, 18, 5, h);
      for (const x of [6, 9, 10, 14, 16]) f.set(x, 1, h);
      f.rect(7, 6, 8, 6, h);
      f.rect(12, 6, 13, 6, h);
      f.rect(5, 6, 5, 8, h);
      f.rect(18, 6, 18, 8, h);
      break;
    case "mohawk":
      f.rect(10, 0, 13, 4, h);
      f.rect(11, 0, 12, 0, hi);
      f.rect(6, 4, 9, 4, shade(t.skin, 0.85));
      f.rect(14, 4, 17, 4, shade(t.skin, 0.85));
      break;
    case "long":
    case "wavy":
    case "bob":
      f.rect(5, 2, 18, 5, h);
      f.rect(5, 6, 8, 6, h);
      f.rect(15, 6, 18, 6, h);
      if (t.hair === "bob") {
        f.rect(4, 5, 6, 12, h);
        f.rect(17, 5, 19, 12, h);
      } else {
        // Front strands over the shoulders.
        for (let y = 5; y <= 19; y++) {
          const off = t.hair === "wavy" && y % 3 === 0 ? 1 : 0;
          f.rect(4 - off, y, 6 - off, y, h);
          f.rect(17 + off, y, 19 + off, y, h);
        }
      }
      f.rect(8, 3, 11, 3, hi);
      break;
    case "ponytail":
      f.rect(5, 2, 18, 5, h);
      f.rect(5, 6, 5, 9, h);
      f.rect(18, 6, 18, 9, h);
      f.rect(8, 3, 11, 3, hi);
      break;
    case "buns":
      f.rect(5, 2, 18, 5, h);
      f.rect(3, 0, 6, 3, h);
      f.rect(17, 0, 20, 3, h);
      f.rect(5, 6, 5, 8, h);
      f.rect(18, 6, 18, 8, h);
      f.rect(4, 1, 5, 1, hi);
      f.rect(18, 1, 19, 1, hi);
      break;
    case "hidden":
      break;
  }
}

function drawHeadwearFront(c: Ctx) {
  const { f, t } = c;
  const col = t.headwearColor;
  switch (t.headwear) {
    case "cap":
      f.rect(5, 1, 18, 5, col);
      f.rect(4, 6, 15, 6, shade(col, 0.75));
      f.set(11, 3, "#f4f4f5");
      f.set(12, 3, "#f4f4f5");
      break;
    case "backcap":
      f.rect(5, 1, 18, 5, col);
      f.rect(5, 5, 18, 5, shade(col, 0.8));
      f.rect(10, 4, 13, 4, shade(col, 0.6)); // strap gap
      break;
    case "beanie":
      f.rect(5, 1, 18, 6, col);
      f.rect(5, 5, 18, 6, shade(col, 0.78));
      f.rect(11, 0, 12, 0, "#f4f4f5");
      break;
    case "crown":
      f.rect(6, 1, 17, 3, GOLD);
      for (const x of [6, 9, 10, 13, 14, 17]) f.set(x, 0, GOLD);
      f.set(11, 2, "#dc2626");
      f.set(12, 2, "#dc2626");
      f.rect(6, 3, 17, 3, shade(GOLD, 0.75));
      break;
    case "headset":
      f.rect(6, 1, 17, 1, "#2a2a33");
      f.rect(4, 7, 5, 10, "#2a2a33");
      f.rect(18, 7, 19, 10, "#2a2a33");
      f.set(4, 8, t.accent);
      f.rect(6, 11, 8, 11, "#2a2a33");
      break;
  }
}

// =====================================================================
// BACK (walking away)
// =====================================================================

function drawBack(c: Ctx) {
  const { f, t, female, frame } = c;
  const tx0 = female ? 8 : 7;
  const tx1 = female ? 15 : 16;
  drawLegsFront(c, tx0, tx1, frame === 1, frame === 3);
  f.rect(tx0, 14, tx1, 22, shade(t.topColor, 0.92));
  f.rect(tx0, 22, tx1, 22, shade(t.topColor, 0.8));
  if (t.top === "hoodie" && t.headwear !== "hood") f.rect(tx0 + 2, 14, tx1 - 2, 16, shade(t.topColor, 0.75));
  if (t.top === "tracksuit") f.rect(tx0, 16, tx1, 16, "#f4f4f5");
  const armDy = frame === 1 ? -1 : frame === 3 ? 1 : 0;
  drawArmFront(c, female ? 6 : 5, armDy, false);
  drawArmFront(c, female ? 16 : 17, -armDy, true);

  const hooded = t.headwear === "hood";
  if (hooded) {
    const hood = t.headwearColor;
    f.rect(4, 1, 19, 14, shade(hood, 0.92));
    for (const [x, y] of [[4, 1], [19, 1], [5, 1], [18, 1], [4, 14], [19, 14]]) f.px[(y + 1) * FRAME_W + x] = null;
    f.rect(11, 3, 12, 12, shade(hood, 0.8)); // seam
    return;
  }

  f.rect(6, 3, 17, 13, t.skin);
  for (const [x, y] of [[6, 3], [17, 3], [6, 13], [17, 13]]) f.px[(y + 1) * FRAME_W + x] = null;
  const h = t.hairColor;
  const d = shade(h, 0.85);
  switch (t.hair) {
    case "buzz":
      f.rect(6, 3, 17, 7, h);
      break;
    case "spiky":
      f.rect(6, 3, 17, 9, h);
      for (const x of [7, 10, 13, 16]) f.rect(x, 1, Math.min(17, x + 1), 2, h);
      break;
    case "sidepart":
    case "messy":
      f.rect(5, 2, 18, 10, h);
      if (t.hair === "messy") for (const x of [6, 9, 14, 16]) f.set(x, 1, h);
      f.rect(5, 10, 18, 10, d);
      break;
    case "mohawk":
      f.rect(10, 0, 13, 12, h);
      break;
    case "long":
    case "wavy":
      f.rect(4, 2, 19, 19, h);
      for (let y = 6; y <= 19; y += 3) f.rect(11, y, 12, y + 1, d);
      if (t.hair === "wavy") for (let y = 5; y <= 19; y += 3) {
        f.set(3, y, h);
        f.set(20, y, h);
      }
      break;
    case "bob":
      f.rect(4, 2, 19, 12, h);
      f.rect(4, 12, 19, 12, d);
      break;
    case "ponytail":
      f.rect(5, 2, 18, 9, h);
      f.rect(10, 9, 13, 14, h);
      f.rect(11, 15, 12, 17, h);
      f.rect(10, 9, 13, 9, "#dc2626"); // hair tie
      break;
    case "buns":
      f.rect(5, 2, 18, 10, h);
      f.rect(3, 0, 6, 3, h);
      f.rect(17, 0, 20, 3, h);
      break;
  }
  // Round the top corners of blocky back hair.
  for (let y = 0; y <= 3; y++) {
    const row = (y + 1) * FRAME_W;
    let x0 = 0;
    while (x0 < FRAME_W && !f.px[row + x0]) x0++;
    let x1 = FRAME_W - 1;
    while (x1 >= 0 && !f.px[row + x1]) x1--;
    if (x1 - x0 >= 8 && y >= 2) {
      const below = (y + 2) * FRAME_W;
      if (!f.px[row - FRAME_W + x0]) f.px[row + x0] = f.px[below + x0] ? null : f.px[row + x0];
      if (!f.px[row - FRAME_W + x1]) f.px[row + x1] = f.px[below + x1] ? null : f.px[row + x1];
    }
  }
  switch (t.headwear) {
    case "cap":
      f.rect(5, 1, 18, 5, t.headwearColor);
      f.rect(10, 5, 13, 5, shade(t.headwearColor, 0.6));
      break;
    case "backcap":
      f.rect(5, 1, 18, 5, t.headwearColor);
      f.rect(8, 6, 19, 6, shade(t.headwearColor, 0.75)); // brim faces backwards
      break;
    case "beanie":
      f.rect(5, 1, 18, 6, t.headwearColor);
      f.rect(5, 5, 18, 6, shade(t.headwearColor, 0.78));
      f.rect(11, 0, 12, 0, "#f4f4f5");
      break;
    case "crown":
      f.rect(6, 1, 17, 3, GOLD);
      for (const x of [6, 9, 10, 13, 14, 17]) f.set(x, 0, GOLD);
      break;
    case "headset":
      f.rect(6, 1, 17, 1, "#2a2a33");
      f.rect(4, 7, 5, 10, "#2a2a33");
      f.rect(18, 7, 19, 10, "#2a2a33");
      break;
  }
  if (t.mask === "blankmask" || t.mask === "visor") f.rect(6, 8, 17, 8, "#2a2a33"); // strap
}

// =====================================================================
// SIDE (facing left; right is mirrored)
// =====================================================================

function drawSide(c: Ctx) {
  const { f, t, female, frame } = c;
  const hooded = t.headwear === "hood";
  const pants = t.bottomColor;
  const stride = frame === 1 || frame === 3;
  const swap = frame === 3;

  // Long hair / ponytail behind
  if (!hooded) {
    if (t.hair === "long" || t.hair === "wavy") f.rect(12, 5, 18, 19, shade(t.hairColor, 0.8));
    if (t.hair === "ponytail") {
      f.rect(16, 6, 18, 12, t.hairColor);
      f.rect(17, 13, 19, 16, t.hairColor);
      f.set(16, 6, "#dc2626");
    }
  }

  // Back arm (darker), drawn before torso
  const backArmX = stride ? (swap ? 9 : 13) : 12;
  armSide(c, backArmX, true);

  // Legs
  const legPx = (x: number, y: number, dark: boolean) => {
    let col = t.bottom === "pants" || y < 26 ? pants : t.skin;
    if (t.bottom === "skirt" && y <= 25) return;
    if (dark) col = shade(col, 0.75);
    f.rect(x, y, x + 2, y, col);
  };
  const shoe = (x: number, dark: boolean) => {
    const s = dark ? shade(t.shoes, 0.75) : t.shoes;
    f.rect(x - 1, 30, x + 2, 31, s);
    f.rect(x - 1, 31, x + 2, 31, shade(s, 0.75));
  };
  if (!stride) {
    for (let y = 24; y <= 29; y++) legPx(10, y, false);
    shoe(10, false);
  } else {
    // Back leg first, then front leg.
    const backX = 13;
    const frontX = 8;
    for (let y = 24; y <= 29; y++) legPx(backX, y, true);
    shoe(backX, true);
    for (let y = 24; y <= 29; y++) legPx(frontX, y, false);
    shoe(frontX, false);
  }

  // Hips + torso
  if (t.bottom === "skirt") {
    f.rect(8, 22, 15, 25, pants);
    f.rect(8, 25, 15, 25, shade(pants, 0.8));
  } else f.rect(9, 22, 14, 23, pants);
  const top = t.topColor;
  f.rect(9, 14, 14, 22, top);
  f.rect(14, 14, 14, 22, shade(top, 0.8));
  if (female) f.rect(8, 16, 8, 18, top); // bust profile
  if (t.top === "suit") {
    f.rect(9, 14, 10, 17, "#f4f4f5");
    f.rect(9, 15, 9, 19, "#dc2626");
  }
  if (t.top === "hoodie" && !hooded) f.rect(12, 14, 15, 16, shade(top, 0.75));
  if (t.top === "tracksuit") f.rect(9, 14, 9, 22, shade(top, 1.35));
  if (t.neck !== "none") {
    f.rect(8, 14, 10, 14, t.neck === "diamond" ? DIAMOND : GOLD);
    f.set(9, 15, t.neck === "diamond" ? "#ffffff" : GOLD);
  }

  // Front arm
  const frontArmX = stride ? (swap ? 13 : 9) : 11;
  armSide(c, frontArmX, false);

  // Head
  if (hooded) {
    const hood = t.headwearColor;
    f.rect(7, 1, 18, 14, hood);
    for (const [x, y] of [[7, 1], [18, 1], [18, 14], [8, 1]]) f.px[(y + 1) * FRAME_W + x] = null;
    f.rect(17, 2, 18, 13, shade(hood, 0.8));
    f.rect(7, 5, 12, 13, t.skin);
    f.rect(7, 4, 13, 4, shade(hood, 1.15));
    if (t.mask !== "none") drawMaskSide(c);
    else faceSide(c);
    return;
  }
  f.rect(7, 3, 16, 13, t.skin);
  f.set(6, 9, t.skin); // nose
  f.rect(16, 4, 16, 13, shade(t.skin, 0.88));
  for (const [x, y] of [[7, 3], [16, 3], [16, 13]]) f.px[(y + 1) * FRAME_W + x] = null;
  f.rect(12, 8, 12, 9, shade(t.skin, 0.8)); // ear
  faceSide(c);
  hairSide(c);
  headwearSide(c);
  if (t.earring) f.set(12, 11, GOLD);
}

function armSide(c: Ctx, x: number, back: boolean) {
  const { f, t } = c;
  const sleeveEnd = t.top === "tee" ? 16 : 20;
  for (let y = 15; y <= 20; y++) {
    let col = y <= sleeveEnd ? t.topColor : t.skin;
    if (back) col = shade(col, 0.75);
    f.rect(x, y, x + 1, y, col);
  }
  f.rect(x, 21, x + 1, 22, back ? shade(t.skin, 0.8) : t.skin);
}

function faceSide(c: Ctx) {
  const { f, t, female } = c;
  f.rect(8, 8, 8, 9, EYE);
  if (female) f.set(7, 8, EYE);
  f.set(7, 11, shade(t.skin, 0.65));
  if (t.beard) f.rect(7, 11, 12, 13, t.hairColor);
  switch (t.eyes) {
    case "shades":
      f.rect(6, 8, 10, 9, "#0f0f14");
      f.rect(11, 8, 12, 8, "#0f0f14");
      break;
    case "laser":
      f.rect(7, 8, 8, 9, "#ff2a2a");
      f.set(8, 8, "#ffe1e1");
      f.set(5, 9, "#ff7a7a");
      break;
    case "glasses3d":
      f.rect(6, 8, 9, 9, "#ef4444");
      f.rect(10, 8, 12, 8, "#f4f4f5");
      break;
  }
}

function drawMaskSide(c: Ctx) {
  const { f, t, noise } = c;
  switch (t.mask) {
    case "balaclava":
      f.rect(7, 5, 12, 13, "#23232b");
      f.rect(7, 8, 10, 9, t.skin);
      f.rect(8, 8, 8, 9, EYE);
      break;
    case "blankmask":
      f.rect(7, 5, 11, 13, "#f2f2f2");
      f.rect(8, 8, 9, 8, "#111");
      f.rect(7, 11, 8, 11, "#8a8a95");
      break;
    case "visor":
      f.rect(6, 7, 11, 9, t.accent);
      f.rect(6, 7, 11, 7, shade(t.accent, 1.5));
      break;
    case "void":
      f.rect(7, 5, 12, 13, "#0b0b10");
      f.rect(7, 8, 8, 8, t.accent);
      break;
    case "glitch": {
      const pal = ["#0b0b10", t.accent, "#f4f4f5", shade(t.accent, 0.5), "#ff2bd6"];
      for (let y = 5; y <= 13; y++) for (let x = 7; x <= 12; x++) f.set(x, y, pal[noise.int(0, pal.length - 1)]);
      f.rect(8, 8, 8, 9, "#ffffff");
      break;
    }
  }
}

function hairSide(c: Ctx) {
  const { f, t } = c;
  const h = t.hairColor;
  switch (t.hair) {
    case "buzz":
      f.rect(7, 3, 16, 4, h);
      f.rect(13, 5, 16, 7, h);
      break;
    case "spiky":
      f.rect(7, 3, 16, 5, h);
      f.rect(13, 6, 16, 9, h);
      for (const x of [8, 11, 14]) f.rect(x, 1, x + 1, 2, h);
      f.rect(16, 2, 17, 3, h);
      break;
    case "sidepart":
    case "messy":
      f.rect(6, 2, 17, 5, h);
      f.rect(7, 6, 9, 6, h);
      f.rect(13, 6, 17, 10, h);
      if (t.hair === "messy") for (const x of [7, 10, 13, 16]) f.set(x, 1, h);
      break;
    case "mohawk":
      f.rect(9, 0, 16, 3, h);
      f.rect(15, 4, 16, 8, h);
      break;
    case "long":
    case "wavy":
    case "bob":
      f.rect(6, 2, 17, 5, h);
      f.rect(7, 6, 9, 6, h);
      f.rect(13, 6, 18, t.hair === "bob" ? 12 : 19, h);
      break;
    case "ponytail":
      f.rect(6, 2, 17, 5, h);
      f.rect(13, 6, 16, 8, h);
      break;
    case "buns":
      f.rect(6, 2, 17, 5, h);
      f.rect(13, 6, 17, 8, h);
      f.rect(14, 0, 17, 3, h);
      break;
  }
}

function headwearSide(c: Ctx) {
  const { f, t } = c;
  const col = t.headwearColor;
  switch (t.headwear) {
    case "cap":
      f.rect(6, 1, 17, 5, col);
      f.rect(3, 6, 8, 6, shade(col, 0.75)); // brim forward
      break;
    case "backcap":
      f.rect(6, 1, 17, 5, col);
      f.rect(15, 6, 20, 6, shade(col, 0.75)); // brim backward
      break;
    case "beanie":
      f.rect(6, 1, 17, 6, col);
      f.rect(6, 5, 17, 6, shade(col, 0.78));
      f.rect(11, 0, 12, 0, "#f4f4f5");
      break;
    case "crown":
      f.rect(7, 1, 16, 3, GOLD);
      for (const x of [7, 10, 13, 16]) f.set(x, 0, GOLD);
      f.set(9, 2, "#dc2626");
      break;
    case "headset":
      f.rect(7, 1, 16, 1, "#2a2a33");
      f.rect(11, 7, 13, 10, "#2a2a33");
      f.set(12, 8, t.accent);
      f.rect(7, 11, 10, 11, "#2a2a33");
      break;
  }
}

// =====================================================================
// sheet assembly
// =====================================================================

function drawFrame(av: AvatarIdentity, dir: Dir, frame: number): Frame {
  const f = new Frame();
  const c: Ctx = {
    f,
    t: av.traits,
    female: av.body === "female",
    frame,
    noise: Rng.fromSeed((av.seed ^ 0x9e37) + dir * 7),
  };
  if (dir === DIR_DOWN) drawFront(c);
  else if (dir === DIR_UP) drawBack(c);
  else drawSide(c);
  f.outline();
  return f;
}

/** Render the full 4x4 sprite sheet for an avatar. Also reports clipped pixels (should be 0). */
export function renderSpriteSheet(av: AvatarIdentity): SpriteSheet & { clipped: number } {
  const data = new Uint8ClampedArray(SHEET_W * SHEET_H * 4);
  let clipped = 0;
  const put = (frm: Frame, col: number, row: number, mirror: boolean) => {
    for (let y = 0; y < FRAME_H; y++) {
      for (let x = 0; x < FRAME_W; x++) {
        const c = frm.px[y * FRAME_W + (mirror ? FRAME_W - 1 - x : x)];
        if (!c) continue;
        const [r, g, b] = parse(c);
        const i = ((row * FRAME_H + y) * SHEET_W + col * FRAME_W + x) * 4;
        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = 255;
      }
    }
  };
  for (let frame = 0; frame < FRAMES; frame++) {
    const down = drawFrame(av, DIR_DOWN, frame);
    const side = drawFrame(av, DIR_LEFT, frame);
    const up = drawFrame(av, DIR_UP, frame);
    clipped += down.clipped + side.clipped + up.clipped;
    put(down, frame, DIR_DOWN, false);
    put(side, frame, DIR_LEFT, false);
    put(side, frame, DIR_RIGHT, true);
    put(up, frame, DIR_UP, false);
  }
  return { width: SHEET_W, height: SHEET_H, data, clipped };
}
